import React, { useState, useEffect, useRef } from "react";
import {
  PieChart, Pie, Cell, Label, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Clock, Database, Globe, FileText, Image, TrendingUp,
  CheckCircle, AlertCircle, Shield, ExternalLink, ClipboardCheck,
  Users, AlertTriangle, RefreshCw, Activity,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const API_BASE = "";

function formatTime(ms) {
  if (!ms || ms <= 0) return '0s';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

const TIMELINE_LABELS = {
  daily: 'Today',
  weekly: '7 Days',
  monthly: '30 Days',
  all: 'Overall',
};

const RISK_COLORS = {
  low: '#22c55e',
  medium: '#f59e0b',
  high: '#ef4444',
};

const STATUS_COLOR = '#3b82f6';

/** Platform switcher: viewing_tenant_id scopes KPIs; empty = Paylegit Core (all tenants). */
function withTenantScope(url) {
  const tenantId = (localStorage.getItem('viewing_tenant_id') || '').trim();
  if (!tenantId || ['all', 'core', 'null', 'undefined'].includes(tenantId.toLowerCase())) {
    return url;
  }
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}tenant_id=${encodeURIComponent(tenantId)}`;
}

/** Distinct bar colors for Top 20 MCC rows (cycled by rank) — high-contrast so ranks read apart. */
const TOP_MCC_BAR_COLORS = [
  '#1d4ed8', // blue
  '#047857', // emerald
  '#c2410c', // orange
  '#b91c1c', // red
  '#0e7490', // cyan
  '#a21caf', // fuchsia
  '#ca8a04', // yellow
  '#166534', // green
  '#9f1239', // rose
  '#4338ca', // indigo
  '#115e59', // teal
  '#9a3412', // brown
  '#0369a1', // sky
  '#854d0e', // gold
  '#be185d', // pink
  '#3f6212', // lime
  '#7c2d12', // rust
  '#1e3a8a', // navy
  '#86198f', // purple
  '#991b1b', // crimson
];

function topMccBarColor(rank) {
  return TOP_MCC_BAR_COLORS[(Math.max(1, rank) - 1) % TOP_MCC_BAR_COLORS.length];
}

function AnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [merchantTimings, setMerchantTimings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [systemHealth, setSystemHealth] = useState(null);
  const [timeline, setTimeline] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [staleWarning, setStaleWarning] = useState(null);
  const intervalRef = useRef(null);
  const hasAnalyticsRef = useRef(false);

  const fetchAnalytics = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const response = await fetch(withTenantScope(`${API_BASE}/api/analytics?timeline=${timeline}`));
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
        hasAnalyticsRef.current = true;
        setError(null);
        setStaleWarning(null);
      } else if (!hasAnalyticsRef.current) {
        setError('Failed to fetch analytics data');
      } else {
        setStaleWarning('Latest dashboard refresh failed. Showing last loaded data.');
      }

      // Fetch hybrid merchant timings
      try {
        const hybridResponse = await fetch(withTenantScope(`${API_BASE}/api/hybrid/merchants`));
        if (hybridResponse.ok) {
          const hybridData = await hybridResponse.json();
          setMerchantTimings(hybridData.merchants || []);
        }
      } catch (err) {
        console.warn('Could not fetch hybrid merchant timings:', err);
      }

      // Fetch system health
      try {
        const healthResponse = await fetch(`${API_BASE}/api/v1/admin/health`);
        if (healthResponse.ok) {
          const healthData = await healthResponse.json();
          setSystemHealth(healthData.data || healthData);
        }
      } catch (err) {
        console.warn('Could not fetch system health:', err);
      }

      setLastUpdated(new Date());
    } catch (err) {
      if (!hasAnalyticsRef.current) {
        setError('Network error while fetching analytics');
      } else {
        setStaleWarning('Latest dashboard refresh failed. Showing last loaded data.');
      }
      console.error('Error fetching analytics:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics({ silent: false });
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchAnalytics({ silent: true }), 30000);
    }
    return () => clearInterval(intervalRef.current);
  }, [timeline, autoRefresh]);

  // --- Derived chart data ---
  const riskPieData = analytics ? [
    { name: 'Low Risk',    value: analytics.risk_levels?.low    || 0, color: RISK_COLORS.low },
    { name: 'Medium Risk', value: analytics.risk_levels?.medium || 0, color: RISK_COLORS.medium },
    { name: 'High Risk',   value: analytics.risk_levels?.high   || 0, color: RISK_COLORS.high },
  ] : [];

  const topMccData = analytics?.top_mcc || [];

  const topMccBarData = topMccData.map((row) => ({
    name: row.mcc_code,
    category: row.category || 'Uncategorized',
    value: row.count,
  }));

  const topMccMax = topMccBarData.length > 0 ? Math.max(...topMccBarData.map((r) => r.value)) : 1;
  const topMccLeftCol = topMccBarData.slice(0, 10);
  const topMccRightCol = topMccBarData.slice(10, 20);

  const renderTopMccRow = (row, rank) => {
    const pct = Math.max(4, Math.round((row.value / topMccMax) * 100));
    const color = topMccBarColor(rank);
    return (
      <div key={`${row.name}-${rank}`} className="py-1" title={`${row.name} — ${row.category}`}>
        <div className="flex items-center gap-1 min-w-0 text-[10px] leading-tight">
          <span className="text-gray-400 w-3 shrink-0 tabular-nums text-[9px]">{rank}</span>
          <span className="font-bold shrink-0 tabular-nums" style={{ color }}>{row.name}</span>
          <span className="text-gray-600 truncate flex-1 min-w-0">
            {row.category}
            <span className="font-bold ml-0.5" style={{ color }}>({row.value})</span>
          </span>
        </div>
        <div className="ml-4 h-2 rounded-full bg-gray-100 overflow-hidden mt-1 border border-gray-200/80">
          <div
            className="h-full rounded-full"
            style={{ width: `${pct}%`, backgroundColor: color, minWidth: pct > 0 ? '6px' : 0 }}
          />
        </div>
      </div>
    );
  };

  const statusBarData = analytics ? [
    { name: 'Approved',       value: analytics.review_status?.approved       || 0 },
    { name: 'Rejected',       value: analytics.review_status?.rejected       || 0 },
    { name: 'In Process',     value: analytics.review_status?.in_process     || 0 },
    { name: 'Further Review', value: analytics.review_status?.further_review || 0 },
  ].filter(d => d.value > 0) : [];

  const totalMerchants  = analytics?.total_merchants       || 0;
  const highRisk        = analytics?.risk_levels?.high     || 0;
  const lowRisk         = analytics?.risk_levels?.low      || 0;
  const classRate       = analytics?.classification_rate   || 0;
  const classified      = analytics?.classified_merchants  || 0;

  if (loading && !analytics) {
    return (
      <div className="w-full flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  if (error && !analytics) {
    return (
      <div className="w-full">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center space-x-2 text-red-600 mb-3">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
          <Button onClick={() => fetchAnalytics()} variant="outline" size="sm">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-5">

      {staleWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center justify-between gap-3">
          <span className="text-sm text-amber-900">{staleWarning}</span>
          <Button onClick={() => fetchAnalytics({ silent: false })} variant="outline" size="sm">Retry</Button>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Automated MCC Risk Assignment Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Comprehensive merchant risk assessment and security monitoring powered by AI
          </p>
        </div>

        {/* Controls: time filters + auto-refresh */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Time filter pills */}
          <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden bg-slate-100 shadow-sm">
            {[
              { key: 'daily',   label: 'Today'   },
              { key: 'weekly',  label: '7 Days'  },
              { key: 'monthly', label: '30 Days' },
              { key: 'all',     label: 'Overall' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTimeline(key)}
                className={`px-3 py-2 text-xs font-semibold transition-colors ${
                  timeline === key
                    ? 'bg-[#1B365D] text-white'
                    : 'text-slate-700 hover:bg-slate-200 hover:text-[#1B365D]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Auto-refresh indicator */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-3 py-1.5 shadow-sm">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
            <span>{loading ? 'Updating…' : `Just now`}</span>
            <span className="text-gray-300 mx-1">|</span>
            <label className="flex items-center gap-1 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={e => setAutoRefresh(e.target.checked)}
                className="accent-blue-600 w-3 h-3"
              />
              Auto
            </label>
            <span className="text-gray-300 mx-1">|</span>
            <button
              onClick={() => fetchAnalytics()}
              disabled={loading}
              className="flex items-center gap-1 text-[#1B365D] hover:text-[#0f2240] font-semibold disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── 4 Stat Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Merchants — blue */}
        <div className="rounded-xl p-5 text-white shadow-sm bg-gradient-to-br from-blue-500 to-blue-600">
          <div className="flex items-center gap-2 mb-3 opacity-90">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Total Merchants</span>
          </div>
          <p className="text-4xl font-bold mb-1">{totalMerchants}</p>
          <p className="text-xs opacity-75 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {analytics?.new_this_week ?? 0} new this week
          </p>
        </div>

        {/* Risk Assessments — orange */}
        <div className="rounded-xl p-5 text-white shadow-sm bg-gradient-to-br from-orange-400 to-orange-500">
          <div className="flex items-center gap-2 mb-3 opacity-90">
            <ClipboardCheck className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">MCCs Analysed</span>
          </div>
          <p className="text-4xl font-bold mb-1">{classified}</p>
          <p className="text-xs opacity-75 flex items-center gap-1">
            <ClipboardCheck className="w-3 h-3" />
            MCCs analysed to date
          </p>
        </div>

        {/* MCC Risk Levels — light tricolor */}
        <div className="rounded-xl p-5 shadow-sm bg-gradient-to-br from-gray-50 to-slate-100 border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">MCC Risk Levels</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center py-2.5 px-1 bg-red-50 rounded-lg border border-red-100">
              <p className="text-2xl font-bold text-red-600 leading-tight">{analytics?.risk_levels?.high || 0}</p>
              <p className="text-[11px] font-semibold text-red-500 mt-0.5">High</p>
            </div>
            <div className="text-center py-2.5 px-1 bg-yellow-50 rounded-lg border border-yellow-100">
              <p className="text-2xl font-bold text-yellow-500 leading-tight">{analytics?.risk_levels?.medium || 0}</p>
              <p className="text-[11px] font-semibold text-yellow-600 mt-0.5">Medium</p>
            </div>
            <div className="text-center py-2.5 px-1 bg-green-50 rounded-lg border border-green-100">
              <p className="text-2xl font-bold text-green-600 leading-tight">{analytics?.risk_levels?.low || 0}</p>
              <p className="text-[11px] font-semibold text-green-500 mt-0.5">Low</p>
            </div>
          </div>
        </div>

        {/* Compliance Rate — green */}
        <div className="rounded-xl p-5 text-white shadow-sm bg-gradient-to-br from-green-500 to-green-600">
          <div className="flex items-center gap-2 mb-3 opacity-90">
            <Shield className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wider">Compliance Rate</span>
          </div>
          <p className="text-4xl font-bold mb-1">{classRate}%</p>
          <p className="text-xs opacity-75 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            {lowRisk} low risk
          </p>
        </div>
      </div>

      {/* ── Charts Row: 40% risk pie · 60% top MCC ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-4">

        {/* Risk Distribution — Pie (40%) */}
        <Card className="shadow-sm border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Activity className="w-4 h-4 text-blue-500" />
              MCC Risk Distribution
            </CardTitle>
            <CardDescription className="text-xs">Breakdown of merchants by assessed MCC risk level</CardDescription>
          </CardHeader>
          <CardContent>
            {riskPieData.every(d => d.value === 0) ? (
              <div className="flex items-center justify-center h-52 text-gray-400 text-sm">No risk data yet</div>
            ) : (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={riskPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={92}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {riskPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} strokeWidth={0} />
                      ))}
                      <Label
                        content={({ viewBox }) => {
                          const { cx, cy } = viewBox;
                          return (
                            <>
                              <text x={cx} y={cy - 7} textAnchor="middle" fill="#111827" fontSize={28} fontWeight={700}>{totalMerchants}</text>
                              <text x={cx} y={cy + 13} textAnchor="middle" fill="#6b7280" fontSize={11}>Total</text>
                            </>
                          );
                        }}
                        position="center"
                      />
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex gap-2 mt-2 flex-wrap justify-center">
                  {riskPieData.map(d => (
                    <div key={d.name} className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border font-medium" style={{ borderColor: d.color + '66', background: d.color + '18', color: d.color }}>
                      <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: d.color }}></span>
                      {d.name} <span className="font-bold ml-0.5">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 20 MCC with Categories (60%) */}
        <Card className="shadow-sm border-l-4 border-l-blue-500">
          <CardHeader className="pb-1.5 pt-3 px-4">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Top 20 MCC with Categories
            </CardTitle>
            <CardDescription className="text-[10px]">Most frequently assigned MCC codes</CardDescription>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-0">
            {topMccBarData.length === 0 ? (
              <div className="flex items-center justify-center h-52 text-gray-400 text-sm">No MCC assignment data yet</div>
            ) : (
              <div className="grid grid-cols-2 gap-x-3 gap-y-0">
                <div className="space-y-0.5 min-w-0">
                  {topMccLeftCol.map((row, i) => renderTopMccRow(row, i + 1))}
                </div>
                <div className="space-y-0.5 min-w-0 border-l border-gray-100 pl-3">
                  {topMccRightCol.map((row, i) => renderTopMccRow(row, i + 11))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Review Status — full width below risk row */}
      <Card className="shadow-sm border-l-4 border-l-orange-400">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <ClipboardCheck className="w-4 h-4 text-orange-500" />
            Review Status Overview
          </CardTitle>
          <CardDescription className="text-xs">Current merchant review and approval pipeline status</CardDescription>
        </CardHeader>
        <CardContent>
          {statusBarData.length === 0 ? (
            <div className="flex items-center justify-center h-52 text-gray-400 text-sm">No status data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, statusBarData.length * 58)}>
              <BarChart data={statusBarData} layout="vertical" margin={{ left: 8, right: 44, top: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f5f5f5" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: '#374151', fontWeight: 500 }} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: '#f9fafb' }} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={28} label={{ position: 'right', fontSize: 12, fontWeight: 600, fill: '#374151' }}>
                  {statusBarData.map((entry, index) => {
                    const statusColors = {
                      'Approved':       '#22c55e',
                      'Rejected':       '#ef4444',
                      'In Process':     '#3b82f6',
                      'Further Review': '#f59e0b',
                    };
                    return <Cell key={index} fill={statusColors[entry.name] || '#3b82f6'} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Detailed Tabs ── */}
      {analytics && (
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-12 rounded-lg p-1.5 gap-1">
            <TabsTrigger value="overview" className="rounded-md text-sm">Overview</TabsTrigger>
            <TabsTrigger value="url-performance" className="rounded-md text-sm">URL Checks Performance</TabsTrigger>
          </TabsList>

          {/* ── OVERVIEW TAB ── */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            {/* Merchant counts */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { icon: FileText,    color: 'blue',   label: 'Merchants Processed', value: analytics.total_merchants || 0 },
                { icon: CheckCircle, color: 'green',  label: 'Classified',           value: analytics.classified_merchants || 0 },
                { icon: ExternalLink,color: 'orange', label: 'Redirects Detected',   value: analytics.redirects_detected || 0 },
                { icon: TrendingUp,  color: 'emerald',label: 'Success Rate',          value: `${analytics.classification_rate || 0}%` },
              ].map(({ icon: Icon, color, label, value }) => (
                <Card key={label} className="shadow-sm">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg bg-${color}-50`}>
                        <Icon className={`w-4 h-4 text-${color}-600`} />
                      </div>
                      <div>
                        <p className="text-xl font-bold leading-tight">{value}</p>
                        <p className="text-xs text-muted-foreground">{label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Risk Assessment */}
              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Shield className="w-4 h-4 text-red-600" />
                    Risk Assessment
                  </CardTitle>
                  <CardDescription className="text-xs">Distribution by risk level based on MCC classification</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-red-50 rounded-xl border border-red-200">
                      <div className="text-2xl font-bold text-red-600">{analytics?.risk_levels?.high || 0}</div>
                      <p className="text-xs font-medium text-red-600 mt-0.5">High Risk</p>
                      <p className="text-[10px] text-red-400 mt-0.5">Gambling, Adult</p>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                      <div className="text-2xl font-bold text-yellow-600">{analytics?.risk_levels?.medium || 0}</div>
                      <p className="text-xs font-medium text-yellow-600 mt-0.5">Medium Risk</p>
                      <p className="text-[10px] text-yellow-500 mt-0.5">Financial Svcs</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-xl border border-green-200">
                      <div className="text-2xl font-bold text-green-600">{analytics?.risk_levels?.low || 0}</div>
                      <p className="text-xs font-medium text-green-600 mt-0.5">Low Risk</p>
                      <p className="text-[10px] text-green-500 mt-0.5">Standard Biz</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Review Status */}
              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <ClipboardCheck className="w-4 h-4 text-blue-600" />
                    Review Status
                  </CardTitle>
                  <CardDescription className="text-xs">Current status of merchant reviews and approvals</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Approved',       value: analytics?.review_status?.approved || 0,       cls: 'bg-green-50 border-green-200 text-green-600' },
                      { label: 'Rejected',        value: analytics?.review_status?.rejected || 0,       cls: 'bg-red-50 border-red-200 text-red-600' },
                      { label: 'In Process',      value: analytics?.review_status?.in_process || 0,     cls: 'bg-blue-50 border-blue-200 text-blue-600' },
                      { label: 'Further Review',  value: analytics?.review_status?.further_review || 0, cls: 'bg-orange-50 border-orange-200 text-orange-600' },
                    ].map(({ label, value, cls }) => (
                      <div key={label} className={`text-center p-3 rounded-xl border ${cls}`}>
                        <div className="text-2xl font-bold">{value}</div>
                        <p className="text-xs font-medium mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ── URL CHECKS PERFORMANCE TAB ── */}
          <TabsContent value="url-performance" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { icon: Database, color: 'blue',   label: 'Total Crawls',     value: analytics.total_crawls || 0 },
                { icon: Globe,    color: 'green',  label: 'URLs Processed',   value: analytics.total_urls || 0 },
                { icon: FileText, color: 'orange', label: 'Merchants Found',  value: analytics.total_merchants || 0 },
                { icon: Image,    color: 'purple', label: 'Screenshots Taken',value: analytics.total_screenshots || 0 },
              ].map(({ icon: Icon, color, label, value }) => (
                <Card key={label} className="shadow-sm">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg bg-${color}-50`}>
                        <Icon className={`w-4 h-4 text-${color}-600`} />
                      </div>
                      <div>
                        <p className="text-xl font-bold leading-tight">{value}</p>
                        <p className="text-xs text-muted-foreground">{label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Processing Performance */}
              <Card className="shadow-sm border-blue-100">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Clock className="w-4 h-4 text-blue-600" />
                    Merchant Processing Performance
                  </CardTitle>
                  <CardDescription className="text-xs">Pipeline timing from fetch to MCC classification</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-3 mb-4">
                    <div className="text-4xl font-bold text-primary mb-1">
                      {(() => {
                        const valid = merchantTimings.filter(m => m.processing_time_ms > 0);
                        if (valid.length > 0) {
                          const avg = valid.reduce((s, m) => s + (m.processing_time_ms || 0), 0) / valid.length / 1000;
                          return formatTime(((analytics?.avg_fetch_time || 0) + (analytics?.avg_parse_time || 0) + avg) * 1000);
                        }
                        return formatTime((analytics?.avg_merchant_time || 0) * 1000);
                      })()}
                    </div>
                    <p className="text-xs text-muted-foreground">Avg total time per merchant</p>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {(() => {
                      const valid = merchantTimings.filter(m => m.processing_time_ms > 0);
                      const avg = arr => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
                      const stages = valid.length > 0 ? [
                        { label: 'Fetch',  val: formatTime(analytics?.avg_fetch_time || 0),  cls: 'blue' },
                        { label: 'Parse',  val: formatTime(analytics?.avg_parse_time || 0),  cls: 'green' },
                        { label: 'AI',     val: avg(valid.map(m => m.stage1_time_ms||0).filter(Boolean)) > 0 ? `${(avg(valid.map(m=>m.stage1_time_ms||0).filter(Boolean))/1000).toFixed(1)}s` : 'N/A', cls: 'purple' },
                        { label: 'Stage 2',  val: avg(valid.map(m => m.stage2_time_ms||0).filter(Boolean)) > 0 ? `${(avg(valid.map(m=>m.stage2_time_ms||0).filter(Boolean))/1000).toFixed(1)}s` : 'N/A', cls: 'orange' },
                        { label: 'LLM',    val: avg(valid.map(m => m.stage3_time_ms||0).filter(Boolean)) > 0 ? `${(avg(valid.map(m=>m.stage3_time_ms||0).filter(Boolean))/1000).toFixed(1)}s` : 'N/A', cls: 'pink' },
                      ] : [
                        { label: 'Fetch',  val: formatTime(analytics?.avg_fetch_time || 0), cls: 'blue' },
                        { label: 'Parse',  val: formatTime(analytics?.avg_parse_time || 0), cls: 'green' },
                        { label: 'AI',     val: 'N/A', cls: 'purple' },
                        { label: 'Stage 2',  val: 'N/A', cls: 'orange' },
                        { label: 'LLM',    val: 'N/A', cls: 'pink' },
                      ];
                      return stages.map(({ label, val, cls }) => (
                        <div key={label} className={`p-2 bg-${cls}-50 rounded-lg border border-${cls}-200 text-center`}>
                          <p className={`text-[10px] text-${cls}-600 mb-0.5`}>{label}</p>
                          <p className={`text-sm font-bold text-${cls}-600`}>{val}</p>
                        </div>
                      ));
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* Classification Success */}
              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    Classification Success
                  </CardTitle>
                  <CardDescription className="text-xs">MCC classification performance and insights</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-green-600 mb-1">{analytics?.success_rate || 0}%</div>
                    <p className="text-xs text-muted-foreground">
                      {analytics?.classified_merchants || 0} of {analytics?.total_merchants || 0} classified
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-3 bg-green-50 rounded-xl border border-green-200">
                      <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                      <p className="text-xl font-bold text-green-600">{analytics?.classified_merchants || 0}</p>
                      <p className="text-xs text-green-600">Classified</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-xl border border-red-200">
                      <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                      <p className="text-xl font-bold text-red-600">
                        {(analytics?.total_merchants || 0) - (analytics?.classified_merchants || 0)}
                      </p>
                      <p className="text-xs text-red-600">Unclassified</p>
                    </div>
                  </div>
                  <div className="pt-2 border-t space-y-1.5">
                    {[
                      ['Avg Fetch Time',  formatTime(analytics?.avg_fetch_time || 0)],
                      ['Avg Parse Time',  formatTime(analytics?.avg_parse_time || 0)],
                      ['Total Processing', formatTime(analytics?.avg_merchant_time || 0)],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between text-xs">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* System Status */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-gray-700">System Status</CardTitle>
                <CardDescription className="text-xs">Current system health and operational status</CardDescription>
              </CardHeader>
              <CardContent>
                {systemHealth ? (
                  <div className="space-y-4">
                    <div className="text-center">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                        systemHealth.overall_status === 'healthy'  ? 'bg-green-100 text-green-800' :
                        systemHealth.overall_status === 'warning'  ? 'bg-yellow-100 text-yellow-800' :
                        systemHealth.overall_status === 'critical' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {systemHealth.overall_status === 'healthy'  && <CheckCircle className="w-3 h-3" />}
                        {systemHealth.overall_status !== 'healthy'  && <AlertCircle className="w-3 h-3" />}
                        <span className="capitalize">{systemHealth.overall_status || 'Unknown'}</span>
                      </span>
                      <p className="text-xs text-muted-foreground mt-2">
                        Last check: {systemHealth.timestamp ? new Date(systemHealth.timestamp).toLocaleString() : 'Unknown'}
                      </p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center p-3 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="text-xl font-bold text-blue-600">{systemHealth.system?.cpu_percent?.toFixed(1) || 0}%</div>
                        <p className="text-xs text-blue-600">CPU Usage</p>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-xl border border-green-200">
                        <div className="text-xl font-bold text-green-600">{systemHealth.system?.memory_percent?.toFixed(1) || 0}%</div>
                        <p className="text-xs text-green-600">Memory</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-xl border border-purple-200">
                        <div className="text-xl font-bold text-purple-600">{systemHealth.docker?.running || 0}</div>
                        <p className="text-xs text-purple-600">Containers</p>
                      </div>
                    </div>
                    {systemHealth.alerts?.length > 0 && (
                      <div className="border-t pt-3">
                        <p className="text-xs font-semibold text-red-600 mb-2">Active Alerts</p>
                        <div className="space-y-1.5">
                          {systemHealth.alerts.slice(0, 3).map((alert, i) => (
                            <div key={i} className={`p-2 rounded-lg border text-xs ${
                              alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                              alert.severity === 'warning'  ? 'bg-yellow-50 border-yellow-200' :
                              'bg-blue-50 border-blue-200'
                            }`}>
                              <span className="font-medium">{alert.component}:</span> {alert.message}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { icon: AlertCircle, label: 'Analytics API',    note: 'Status Unknown' },
                      { icon: Database,    label: 'Database',          note: 'Status Unknown' },
                      { icon: Clock,       label: 'Real-time Updates', note: 'Active (30s)' },
                    ].map(({ icon: Icon, label, note }) => (
                      <div key={label} className="text-center">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Icon className="w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-xs font-semibold text-gray-600">{label}</p>
                        <p className="text-xs text-muted-foreground">{note}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

export default AnalyticsPage;
